import { useMediaQuery, Paper } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useTheme, alpha } from '@mui/material/styles';
import LogoImage from './LogoImage';
import AnimatedVehicles from './AnimatedVehicles';

const useStyles = makeStyles()((theme) => ({
  root: {
    display: 'flex',
    minHeight: '100vh',
    position: 'relative',
    overflow: 'hidden',
  },

  // Barra/Logo (direita) — some em telas menores que 'lg'
  sidebar: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    background: theme.palette.primary.main,
    paddingBottom: theme.spacing(5),
    width: theme.dimensions.sidebarWidth,
    [theme.breakpoints.down('lg')]: {
      display: 'none',
      width: 0,
      paddingBottom: 0,
    },
  },

  // Card do formulário (esquerda)
  paper: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    flex: 1,
    background: theme.palette.background.default,
    borderRadius: 20,
    boxShadow: `2px 0px 16px rgba(0,0,0,.25),
                0 0 0 1px ${alpha(theme.palette.common.white, .06)}`,
    [theme.breakpoints.up('lg')]: {
      padding: theme.spacing(0, 20, 0, 6),
      margin: theme.spacing(3),
    },
    [theme.breakpoints.down('lg')]: {
      boxShadow: 'none',
      padding: 0,
      margin: 0,
      borderRadius: 0,
    },
  },

  // Header com a logo (apenas mobile/tablet)
  mobileHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: theme.spacing(4.5),
    paddingBottom: theme.spacing(1),
    [theme.breakpoints.up('lg')]: {
      display: 'none',
    },
  },

  // Container interno do form
  form: {
    maxWidth: 520,
    width: '100%',
    padding: theme.spacing(5),
    [theme.breakpoints.down('lg')]: {
      maxWidth: 'min(92vw, 520px)',
      padding: theme.spacing(4, 3, 10),
      margin: '0 auto',
    },

    '& .MuiTextField-root': {
      transition: 'transform .2s ease',
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: 14,
      transition: 'box-shadow .2s ease, transform .2s ease',
    },
    '& .MuiOutlinedInput-root.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: theme.palette.secondary.main,
      boxShadow: `0 0 0 3px ${alpha(theme.palette.secondary.main, 0.18)} inset`,
    },
    '& .MuiOutlinedInput-root:hover': {
      transform: 'translateY(-1px)',
    },
    '& .MuiButton-root': {
      marginTop: theme.spacing(1),
      padding: theme.spacing(1.4),
      borderRadius: 14,
      textTransform: 'none',
      fontWeight: 700,
      letterSpacing: '.3px',
      boxShadow: `0 6px 18px ${alpha(theme.palette.secondary.main, 0.18)}`,
    },
  },

  // Logo centralizada e com tamanho maior no mobile
  logoBox: {
    display: 'flex',
    justifyContent: 'center',
    maxWidth: '80%',
    margin: '0 auto',
    [theme.breakpoints.down('lg')]: {
      maxWidth: 280,               // ↑ aumentei a logo no mobile
      marginBottom: theme.spacing(1.5),
    },
    '& img, & svg': {
      maxWidth: '100%',
      height: 'auto',
      display: 'block',            // evita “sobras” laterais
    },
  },
}));

const LoginLayout = ({ children }) => {
  const { classes } = useStyles();
  const theme = useTheme();
  const showSidebar = useMediaQuery(theme.breakpoints.up('lg'));

  return (
    <main className={classes.root}>
      {/* Formulário (sempre visível) + logo no topo quando não há sidebar */}
      <Paper className={classes.paper} elevation={showSidebar ? 3 : 0}>
        {!showSidebar && (
          <div className={classes.mobileHeader}>
            <div className={classes.logoBox}>
              <LogoImage />
            </div>
          </div>
        )}
        <form className={classes.form}>{children}</form>
      </Paper>

      {/* Barra/Logo à direita apenas no desktop */}
      {showSidebar && (
        <div className={classes.sidebar}>
          <div className={classes.logoBox}>
            <LogoImage color={theme.palette.secondary.contrastText} />
          </div>
        </div>
      )}

      {/* Pista animada no rodapé (sempre) */}
      <AnimatedVehicles />
    </main>
  );
};

export default LoginLayout;
